// ============================================================
// settingsUi.tsx — shared primitives for the Settings redesign
// (Option B: left section-nav + card panels). Every value is copied from
// the approved mockup (casevo-settings-redesign.html) via Tailwind
// arbitrary syntax, so the Settings page and its sub-panels
// (TwoFactorPanel, MicrosoftPanel, EmailSignaturePanel,
// UserTimelineColoursPanel) share ONE card / row / switch / badge /
// button recipe. Token colours → light + dark automatically; the few
// mockup dark-only overrides (#7fd1a8 / #74c69d accents, the #31443a
// "track" badge) are explicit dark: variants.
// ============================================================
import React from 'react'
import { BTN_PRIMARY } from '../../styles/buttons'

// ── Card ─────────────────────────────────────────────────────
// .card: surface, 1px border, 14px radius, the standard card shadow.
export const SETTINGS_CARD =
  'bg-casevo-surface border border-casevo-border rounded-[14px] ' +
  'shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] ' +
  'dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'

// ── Buttons (.btn / .btn.sm) ─────────────────────────────────
const BTN_SIZE = 'text-[13.5px] rounded-[9px] px-[16px] py-[9px]'
const BTN_SIZE_SM = 'text-[12.5px] rounded-[8px] px-[12px] py-[7px]'
const BTN_BASE =
  'inline-flex items-center gap-[7px] font-semibold whitespace-nowrap border transition-colors ' +
  'disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none casevo-focus'
const GHOST = 'border-casevo-border text-casevo-forest bg-casevo-surface hover:border-casevo-mid'
const DANGER =
  'border-casevo-border text-casevo-danger bg-casevo-surface hover:border-red-300 dark:hover:border-red-400/60'
// Primary = the shared green BTN_PRIMARY recipe (casevo-primary tokens,
// white text, focus ring) at the mockup's .btn sizes.
export const S_BTN_PRIMARY = `${BTN_PRIMARY} border border-transparent whitespace-nowrap ${BTN_SIZE}`
export const S_BTN_PRIMARY_SM = `${BTN_PRIMARY} border border-transparent whitespace-nowrap ${BTN_SIZE_SM}`
export const S_BTN_GHOST = `${BTN_BASE} ${GHOST} ${BTN_SIZE}`
export const S_BTN_GHOST_SM = `${BTN_BASE} ${GHOST} ${BTN_SIZE_SM}`
export const S_BTN_DANGER = `${BTN_BASE} ${DANGER} ${BTN_SIZE}`
export const S_BTN_DANGER_SM = `${BTN_BASE} ${DANGER} ${BTN_SIZE_SM}`

// .link — underlined brand link (dark #74c69d for contrast).
export const S_LINK =
  'text-[13px] font-medium text-casevo-brand dark:text-[#74c69d] underline underline-offset-[3px] ' +
  'hover:opacity-80 disabled:opacity-60 disabled:no-underline disabled:cursor-default focus:outline-none casevo-focus rounded-sm'

// .note — muted helper copy.
export const S_NOTE = 'text-[12.5px] text-casevo-text-soft leading-[1.5]'

// ── Form fields (.fields / .fl / .inp) ──────────────────────
export const S_FIELDS = 'grid grid-cols-1 sm:grid-cols-2 gap-[14px] pt-[14px] pb-[4px]'
// Mockup .fl uses --mid (#40916c) = 3.8:1 on white at 11px → light uses
// brand (6.4:1); dark keeps mid (4.9:1).
export const S_LABEL = 'block text-[11px] font-semibold tracking-[0.1em] uppercase text-casevo-brand dark:text-casevo-mid mb-[6px]'
export const sInput = (hasError = false): string =>
  'w-full h-[40px] border rounded-[9px] bg-casevo-input px-[12px] text-[14px] text-casevo-forest ' +
  'placeholder:text-casevo-text-soft focus:outline-none focus:border-casevo-mid ' +
  'focus:shadow-[0_0_0_3px_rgba(64,145,108,0.15)] disabled:opacity-60 disabled:cursor-not-allowed ' +
  (hasError ? 'border-casevo-danger' : 'border-casevo-border')
export const S_FORMFOOT = 'flex flex-wrap items-center gap-[10px] mt-[16px]'
export const S_FIELD_ERROR = 'text-[12px] text-casevo-danger mt-[5px]'
// (E-sign fixes B) The old tinted red / green banners (S_ERROR_BANNER /
// S_OK_BANNER) are gone: a refusal is ErrorNote, a result is ActionBox
// (components/shared/ActionBox).

// ── Icons (mockup set, 24-grid, stroke 2) ───────────────────
export type SettingsIconName =
  | 'user' | 'shield' | 'key' | 'plug' | 'pen' | 'sun' | 'bell' | 'building' | 'out'
  | 'copy' | 'check' | 'cam' | 'palette' | 'upload' | 'ms'
  // Admin redesign set
  | 'users' | 'mail' | 'pound' | 'flag' | 'history' | 'list' | 'task' | 'brief'
  | 'cal' | 'eye' | 'globe' | 'uplus' | 'search' | 'x' | 'trash' | 'plus' | 'lock'
  | 'refresh' | 'chev' | 'send' | 'edit'
  // Case-page section nav (Stage 1 shell)
  | 'grid' | 'source' | 'car' | 'heart' | 'umbrella' | 'scale' | 'agency' | 'steth' | 'file' | 'link' | 'cog' | 'pulse'
  // Invoicing Stage 1 — the bill (receipt) mark
  | 'bill'
  // Stage 4 — PI settlements (Admin section + case Settlement section)
  | 'percent'

const PATHS: Record<SettingsIconName, React.ReactNode> = {
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></>,
  key: <><circle cx="7.5" cy="15.5" r="4.5" /><path d="M10.7 12.3L21 2M16 7l3 3M14 9l2 2" /></>,
  plug: <path d="M9 2v6M15 2v6M6 8h12v4a6 6 0 0 1-12 0zM12 18v4" />,
  pen: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
  bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></>,
  building: <path d="M3 21h18M6 21V7l6-4 6 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" />,
  out: <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />,
  copy: <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
  check: <path d="M20 6L9 17l-5-5" />,
  cam: <><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>,
  palette: <><circle cx="13.5" cy="6.5" r="1.5" /><circle cx="17.5" cy="10.5" r="1.5" /><circle cx="8.5" cy="7.5" r="1.5" /><circle cx="6.5" cy="12.5" r="1.5" /><path d="M12 2a10 10 0 0 0 0 20c1 0 1.5-.8 1.5-1.5 0-.4-.1-.7-.4-1-.3-.3-.4-.6-.4-1 0-.8.7-1.5 1.5-1.5H16a6 6 0 0 0 6-6c0-5-4.5-9-10-9z" /></>,
  upload: <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />,
  ms: <><rect x="3" y="3" width="8" height="8" /><rect x="13" y="3" width="8" height="8" /><rect x="3" y="13" width="8" height="8" /><rect x="13" y="13" width="8" height="8" /></>,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  mail: <><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /><path d="M22 6l-10 7L2 6" /></>,
  bill: <><path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 .7V2l-1 .7-3-2-3 2-3-2-3 2-3-2z" /><path d="M8 8h8M8 12h8M8 16h4" /></>,
  percent: <><path d="M19 5 5 19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>,
  pound: <path d="M18 7a4 4 0 0 0-7.5-2c-.6 1.3-.5 3 .1 5.5.5 2 .4 4.5-1.6 6.5H18M7 12h8" />,
  flag: <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><path d="M4 22v-7" /></>,
  pulse: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  history: <><path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" /><path d="M12 7v5l4 2" /></>,
  list: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />,
  task: <><path d="M9 11l3 3 8-8" /><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9" /></>,
  brief: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V4h8v3" /></>,
  cal: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,
  globe: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z" /></>,
  uplus: <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><path d="M20 8v6M23 11h-6" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>,
  x: <path d="M18 6L6 18M6 6l12 12" />,
  trash: <path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />,
  plus: <path d="M12 5v14M5 12h14" />,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></>,
  lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  refresh: <><path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15" /></>,
  chev: <path d="M9 18l6-6-6-6" />,
  send: <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />,
  grid: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></>,
  source: <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />,
  car: <><path d="M5 17h14M5 17a2 2 0 1 0 4 0M15 17a2 2 0 1 0 4 0" /><path d="M3 17V11l2-5h14l2 5v6" /></>,
  heart: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />,
  umbrella: <path d="M23 12a11.05 11.05 0 0 0-22 0zm-5 7a3 3 0 0 1-6 0v-7" />,
  scale: <path d="M12 3v18M5 7h14M5 7l-3 7a4 4 0 0 0 6 0zM19 7l-3 7a4 4 0 0 0 6 0z" />,
  agency: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M12 8v8M8 12h8" /></>,
  steth: <><path d="M4 3v6a6 6 0 0 0 12 0V3" /><circle cx="18" cy="16" r="3" /><path d="M10 15v2a4 4 0 0 0 8 0" /></>,
  file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>,
  link: <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />,
  cog: <><circle cx="12" cy="12" r="3" /><path d="M12 1v3M12 20v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M1 12h3M20 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" /></>,
}

export const SIcon: React.FC<{ name: SettingsIconName; size?: number; className?: string }> = ({
  name, size = 16, className = '',
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`shrink-0 ${className}`}
    aria-hidden="true"
  >
    {PATHS[name]}
  </svg>
)

// ── Section nav (Settings / Admin / Client page) ────────────
// ONE source for the redesigned split-page section nav: the desktop nav
// item (raised-card active highlight + primary bar) and the mobile
// scrollable pill. Lifted verbatim from the Settings + Admin inline markup.
const SECTION_ITEM_BASE =
  'relative flex items-center gap-[11px] w-full text-left px-[12px] rounded-[10px] text-[14px] mb-[2px] border transition-colors focus:outline-none casevo-focus'
const SECTION_ITEM_ON =
  'bg-casevo-surface text-casevo-forest font-semibold border-casevo-border shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'
// Mockup idle = --mid (3.6:1 on the light page bg) → light brand (5.9:1);
// dark keeps mid (6.2:1).
const SECTION_ITEM_OFF =
  'border-transparent text-casevo-brand dark:text-casevo-mid hover:bg-casevo-surface/60 hover:text-casevo-forest'
// Locked (case-page setup chain): soft text at 62% (mockup .ni.lock).
const SECTION_ITEM_LOCKED = 'border-transparent text-casevo-text-soft opacity-[0.62] cursor-not-allowed'

export const SectionNavItem: React.FC<{
  icon: SettingsIconName
  label: string
  active: boolean
  onClick: () => void
  // Right-aligned sub-line (count / status), e.g. "3 active".
  sub?: React.ReactNode
  // Admin's denser rhythm (py 9) vs Settings/Client (py 10).
  dense?: boolean
  // (Case page) right-aligned status mark after `sub` (setup tick etc.).
  trailing?: React.ReactNode
  // (Case page) a locked row — greyed, not clickable, `title` says why.
  disabled?: boolean
  title?: string
}> = ({ icon, label, active, onClick, sub, dense, trailing, disabled, title }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    aria-current={active ? 'page' : undefined}
    aria-disabled={disabled || undefined}
    title={title}
    className={`${SECTION_ITEM_BASE} ${dense ? 'py-[9px]' : 'py-[10px]'} ${disabled ? SECTION_ITEM_LOCKED : active ? SECTION_ITEM_ON : SECTION_ITEM_OFF}`}
  >
    {active && (
      <span aria-hidden="true" className="absolute left-[-1px] top-[9px] bottom-[9px] w-[3px] rounded-r-[3px] bg-casevo-primary dark:bg-[#74c69d]" />
    )}
    <SIcon name={icon} size={17} />
    <span className="min-w-0 truncate">{label}</span>
    {trailing !== undefined ? (
      <span className="ml-auto pl-[6px] flex items-center gap-[8px] shrink-0">
        {sub != null && sub !== '' && <small className="text-[11.5px] font-normal text-casevo-text-soft whitespace-nowrap">{sub}</small>}
        {trailing}
      </span>
    ) : (
      sub != null && sub !== '' && <small className="ml-auto pl-[6px] text-[11.5px] font-normal text-casevo-text-soft whitespace-nowrap">{sub}</small>
    )}
  </button>
)

// Mobile / tablet: the nav collapses to a horizontally-scrolling pill bar.
export const SectionNavPill: React.FC<{
  icon: SettingsIconName
  label: string
  active: boolean
  onClick: () => void
  // (Case page) status mark after the label; locked pill.
  trailing?: React.ReactNode
  disabled?: boolean
  title?: string
}> = ({ icon, label, active, onClick, trailing, disabled, title }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    aria-current={active ? 'page' : undefined}
    aria-disabled={disabled || undefined}
    title={title}
    className={`inline-flex items-center gap-[7px] text-[13px] px-[12px] py-[7px] rounded-full border whitespace-nowrap transition-colors focus:outline-none casevo-focus ${
      disabled
        ? 'border-casevo-border bg-casevo-surface text-casevo-text-soft opacity-[0.62] cursor-not-allowed'
        : active
        ? 'border-casevo-brand bg-casevo-mint text-casevo-forest dark:text-[#cfeedd] font-semibold'
        : 'border-casevo-border bg-casevo-surface text-casevo-brand dark:text-casevo-mid hover:border-casevo-mid'
    }`}
  >
    <SIcon name={icon} size={15} />
    {label}
    {trailing}
  </button>
)

// ── Card with header (.card > .ch + .cb) ────────────────────
interface SettingsCardProps {
  icon: SettingsIconName
  title: string
  sub?: React.ReactNode
  right?: React.ReactNode
  // .cb body. Omit for a header-only card (Session).
  children?: React.ReactNode
  // Signature + colours bodies drop the 4px top padding (mockup inline
  // style="padding-top:0").
  flushBody?: boolean
  id?: string
  // (E-sign fixes B) A read-first card being edited: the editing ring, and
  // its Save / Cancel bar along the card's bottom edge (`footer`).
  editing?: boolean
  footer?: React.ReactNode
}

// The editing ring (the same values as ClientInfoSections CARD_EDITING).
const SETTINGS_CARD_EDITING =
  '!border-casevo-mid overflow-hidden !shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent),0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] ' +
  'dark:!shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent),0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'

export const SettingsCard: React.FC<SettingsCardProps> = ({ icon, title, sub, right, children, flushBody, id, editing, footer }) => (
  <section className={`${SETTINGS_CARD}${editing ? ` ${SETTINGS_CARD_EDITING}` : ''}`} id={id} aria-labelledby={id ? `${id}-h` : undefined}>
    <div className="flex flex-wrap items-center gap-[12px] px-[22px] pt-[18px] pb-[14px]">
      <div className="w-[34px] h-[34px] rounded-[10px] bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8] grid place-items-center shrink-0">
        <SIcon name={icon} />
      </div>
      <div className="flex-1 min-w-[160px]">
        <h2 id={id ? `${id}-h` : undefined} className="text-[15.5px] font-semibold text-casevo-forest">{title}</h2>
        {sub && <p className="text-[12.5px] text-casevo-text-soft mt-[2px]">{sub}</p>}
      </div>
      {right && <div className="flex items-center gap-[8px] shrink-0">{right}</div>}
    </div>
    {children !== undefined && children !== null && children !== false && (
      <div className={`px-[22px] pb-[20px] ${flushBody ? 'pt-0' : 'pt-[4px]'}`}>{children}</div>
    )}
    {footer}
  </section>
)

// ── Rows ─────────────────────────────────────────────────────
// .row — label | value | action. Hair divider between rows (not above
// the first). On mobile the label stacks above value + action.
const ROW_DIVIDER = 'border-t border-casevo-border-soft first:border-t-0'

export const SettingsRow: React.FC<{ label: string; children: React.ReactNode; action?: React.ReactNode }> = ({
  label, children, action,
}) => (
  <div className={`grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[170px_minmax(0,1fr)_auto] items-center gap-x-[16px] gap-y-[4px] py-[13px] min-h-[54px] ${ROW_DIVIDER}`}>
    <span className="col-span-2 sm:col-span-1 text-[13px] text-casevo-text-soft">{label}</span>
    <div className="text-[14px] text-casevo-forest font-medium min-w-0 break-words">{children}</div>
    <div className="flex items-center justify-end">{action}</div>
  </div>
)

// Small secondary line inside a row value (.row .v small).
export const RowSmall: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="block font-normal text-[12.5px] text-casevo-text-soft mt-[2px] leading-[1.45]">{children}</span>
)

// .row.tg — title + description | control.
export const SettingsToggleRow: React.FC<{
  title: React.ReactNode
  desc?: React.ReactNode
  control?: React.ReactNode
  titleWeight?: 'medium' | 'normal'
}> = ({ title, desc, control, titleWeight = 'medium' }) => (
  <div className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-[16px] py-[13px] min-h-[54px] ${ROW_DIVIDER}`}>
    <div className="min-w-0">
      <div className={`text-[14px] text-casevo-forest break-words ${titleWeight === 'medium' ? 'font-medium' : 'font-normal'}`}>{title}</div>
      {desc && <div className="text-[12.5px] text-casevo-text-soft mt-[3px] leading-[1.45] max-w-[560px]">{desc}</div>}
    </div>
    {control !== undefined && <div className="flex items-center justify-end">{control}</div>}
  </div>
)

// ── Switch (.sw) — 40×23 track, 18px knob 2.5px in, left 19.5px on ──
export const SettingsSwitch: React.FC<{
  checked: boolean
  onChange: () => void
  label: string
  disabled?: boolean
}> = ({ checked, onChange, label, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={onChange}
    disabled={disabled}
    className={`relative w-[40px] h-[23px] rounded-full shrink-0 transition-colors duration-150 focus:outline-none casevo-focus disabled:opacity-60 ${
      checked ? 'bg-casevo-primary' : 'bg-casevo-border'
    }`}
  >
    <span
      className={`absolute top-[2.5px] w-[18px] h-[18px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-[left] duration-150 ${
        checked ? 'left-[19.5px]' : 'left-[2.5px]'
      }`}
    />
  </button>
)

// ── Badge (.badge.on / .badge.off) ──────────────────────────
export const SettingsBadge: React.FC<{ on?: boolean; dot?: boolean; children: React.ReactNode }> = ({
  on = false, dot = false, children,
}) => (
  <span
    className={`inline-flex items-center gap-[6px] text-[11.5px] font-semibold px-[10px] py-[3px] rounded-full whitespace-nowrap ${
      on
        ? 'bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8]'
        // Mockup .badge.off text = --soft (4.2:1 light / 4.0:1 dark on the
        // track) → nudged to #5c6a61 / #a3bdb0 (4.9:1 / 5.2:1).
        : 'bg-[#e8f1ec] dark:bg-[#31443a] text-[#5c6a61] dark:text-[#a3bdb0]'
    }`}
  >
    {dot && <span className="w-[6px] h-[6px] rounded-full bg-current" aria-hidden="true" />}
    {children}
  </span>
)

// ── "Saved ✓" (.saved) ──────────────────────────────────────
export const SettingsSaved: React.FC<{ children?: React.ReactNode }> = ({ children = 'Saved' }) => (
  <span className="inline-flex items-center gap-[5px] text-[12.5px] font-medium text-casevo-brand dark:text-[#7fd1a8]" role="status">
    <SIcon name="check" size={13} />
    {children}
  </span>
)
